// Логика «Болеем» без React (§2.18 этап 2в): тапы копим у себя и шлём пачкой не больше batch_max; сверх cap за
// матч сервер принимает 0 — дальше не копим и не шлём. Тексты ошибок live-действий — по машинной reason.
import { ApiError } from '../api/client';
import type { GameCheer } from '../api/gameTypes';
import type { StringKey } from '../i18n/i18n';

export type Pending = Record<string, number>;

/** Сколько тапов уже учтено: принятые сервером + ждущие отправки (в том числе улетевшая пачка). */
export function totalTaps(cheer: GameCheer, pending: Pending, inFlight = 0): number {
  return cheer.my_taps + inFlight + Object.values(pending).reduce((a, b) => a + b, 0);
}

/** Тап по стороне: до потолка cap — +1 в очередь; на потолке — очередь как была (null — тап не засчитан). */
export function addTap(cheer: GameCheer, pending: Pending, side: string, inFlight = 0): Pending | null {
  if (totalTaps(cheer, pending, inFlight) >= cheer.cap) return null;
  return { ...pending, [side]: (pending[side] ?? 0) + 1 };
}

/** Следующая пачка: сторона с наибольшей очередью, не больше batch_max и остатка до cap. Нечего слать — null. */
export function takeBatch(cheer: GameCheer, pending: Pending): { side: string; taps: number; rest: Pending } | null {
  const room = Math.max(0, cheer.cap - cheer.my_taps);
  const [side, count] = Object.entries(pending).sort((a, b) => b[1] - a[1])[0] ?? [];
  if (!side || !count || room === 0) return null;
  const taps = Math.min(count, Math.max(1, cheer.batch_max), room);
  const rest = { ...pending, [side]: count - taps };
  if (!rest[side]) delete rest[side];
  return { side, taps, rest };
}

/** Доли сторон для шкалы: percent сервера; null (ещё никто не болел) — поровну. */
export function cheerShares(cheer: GameCheer): Record<string, number> {
  const known = cheer.sides.every((s) => s.percent !== null);
  return Object.fromEntries(cheer.sides.map((s) => [s.side, known ? s.percent! : 100 / cheer.sides.length]));
}

const REASONS: Record<string, StringKey> = {
  over_closed: 'tg_game_err_over_closed',
  cheer_closed: 'tg_game_err_cheer_closed',
};

/** Сбой live-действия: 409 game_action_unavailable — по reason; иное — общий текст. */
export function liveErrorKey(error: unknown): StringKey {
  if (error instanceof ApiError && error.code === 'game_action_unavailable') {
    const reason = error.payload?.reason;
    return (typeof reason === 'string' && REASONS[reason]) || 'tg_world_err_unavailable';
  }
  return 'tg_world_err_generic';
}

export const isCheerClosed = (error: unknown) =>
  error instanceof ApiError && error.status === 409 && error.payload?.reason === 'cheer_closed';
